import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { getWasteStreamRegistrationSubmissionMethod } from "encamp-shared/src/submissions/wasteStreamRegistration";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionMethod } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";

/** Sentinel for "let the server derive the method from the facility's state." */
export const AUTO_SENTINEL = "__AUTO__";
export type MethodOverride = SubmissionMethod | typeof AUTO_SENTINEL;

export interface RegisterNewTabProps {
  facilityName: string;
  state: string;
  methodOverride: MethodOverride;
  onMethodChange: (method: MethodOverride) => void;
}

/**
 * "Register new" tab content: creates (and links, server-side) a fresh
 * IN_PROGRESS placeholder registration for this one stream, reusing
 * `createWasteStreamRegistrationSubmissions` (T6). Assignee and due date are
 * computed server-side; this surfaces them read-only rather than collecting
 * them, mirroring `CreateWasteStreamRegistrationSubmissionsDialog` cut down
 * to a single stream.
 */
export function RegisterNewTab({
  facilityName,
  state,
  methodOverride,
  onMethodChange,
}: RegisterNewTabProps) {
  const { user } = useCurrentUser();
  const resolvedMethod =
    methodOverride === AUTO_SENTINEL
      ? getWasteStreamRegistrationSubmissionMethod(state)
      : methodOverride;

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        Due date is set automatically to the earliest shipment date for this stream plus 30 days.
        Assignee: {user?.email ?? "you"}.
      </Alert>

      <Typography variant="body2" color="text.secondary">
        Facility: {facilityName} · State: {state}
      </Typography>

      <FormControl size="small" sx={{ width: 280 }}>
        <InputLabel>Submission Method</InputLabel>
        <Select<MethodOverride>
          value={methodOverride}
          label="Submission Method"
          onChange={(e: SelectChangeEvent<MethodOverride>) => onMethodChange(e.target.value)}
        >
          <MenuItem value={AUTO_SENTINEL}>
            <em>Auto ({prettyPrintEnumValue(resolvedMethod)})</em>
          </MenuItem>
          {Object.values(SubmissionMethod).map((value) => (
            <MenuItem key={value} value={value}>
              {prettyPrintEnumValue(value)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
